import { yupResolver } from "@hookform/resolvers/yup";
import userImage from "assets/images/user.png";
import { login } from "core/Services/AuthServices";
import { CircleHelp, LogIn, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "shared/Button";
import FormCheckbox from "shared/FormCheckbox";
import FormInput from "shared/FormInput";
import {
  ACCESS_TOKEN,
  USER_EMAIL,
  USER_ID,
  USER_NAME,
  USER_ROLE,
} from "utils/constant";
import * as yup from "yup";

interface MerchantFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

const REMEMBER_ME_KEY = "rememberMe";
const REMEMBERED_EMAIL_KEY = "rememberedEmail";

const LoginForm = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { t } = useTranslation();
  const MerchantLoginSchema = yup.object().shape({
    email: yup
      .string()
      .email(t("errors.validation.invalidEmail"))
      .required(t("errors.validation.emailRequired")),
    password: yup.string().required(t("errors.validation.passwordRequired")),
    rememberMe: yup.boolean().default(false).optional(),
  });
  useEffect(() => {
    const token = localStorage.getItem(ACCESS_TOKEN);
    const role = localStorage.getItem(USER_ROLE);

    if (token && role) {
      switch (role) {
        case "merchant":
          navigate("/merchant-dashboard?pageTitle=Dashboard");
          break;
        case "admin":
          navigate("/admin-dashboard?pageTitle=Dashboard");
          break;
        case "operator":
          navigate("/operator-dashboard?pageTitle=Dashboard");
          break;
        case "accountant":
          navigate("/accountant-dashboard?pageTitle=Dashboard");
          break;
        default:
          navigate("/?pageTitle=Home");
      }
    }
  }, [navigate]);

  const rememberedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY) || "";
  const rememberedChecked = localStorage.getItem(REMEMBER_ME_KEY) === "true";

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<MerchantFormData>({
    resolver: yupResolver(MerchantLoginSchema),
    defaultValues: {
      email: rememberedEmail,
      password: "",
      rememberMe: rememberedChecked,
    },
  });

  const rememberMe = watch("rememberMe");

  useEffect(() => {
    localStorage.setItem(REMEMBER_ME_KEY, rememberMe ? "true" : "false");

    if (!rememberMe) {
      localStorage.removeItem(REMEMBERED_EMAIL_KEY);
    }
  }, [rememberMe]);

  const onSubmit = async (data: MerchantFormData) => {
    try {
      setIsSubmitting(true);
      const response = await login(data.email, data.password);

      if (data.rememberMe) {
        localStorage.setItem(REMEMBERED_EMAIL_KEY, data.email);
      } else {
        localStorage.removeItem(REMEMBERED_EMAIL_KEY);
      }

      localStorage.setItem(ACCESS_TOKEN, response.token);
      localStorage.setItem(USER_ROLE, response.user.role);
      localStorage.setItem(USER_ID, response.user.id.toString());

      localStorage.setItem(
        USER_NAME,
        `${response.user.first_name} ${response.user.last_name}`
      );
      localStorage.setItem(USER_EMAIL, response.user.email);

      switch (response.user.role) {
        case "merchant":
          navigate("/merchant-dashboard?pageTitle=Dashboard");
          break;
        case "admin":
          navigate("/admin-dashboard?pageTitle=Dashboard");
          break;
        case "operator":
          navigate("/operator-dashboard?pageTitle=Dashboard");
          break;
        case "accountant":
          navigate("/accountant-dashboard?pageTitle=Dashboard");
          break;
        case "driver":
          toast.error(t("auth.driverNotSupported"));
          break;
        default:
          navigate("/?pageTitle=Home");
      }
    } catch (error: any) {
      toast.error(error.response.data.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full w-full flex items-center justify-center">
      <div className="px-6 bg-white py-6 rounded-lg shadow-md flex flex-col gap-3 items-center w-full max-w-md">
        <img src={userImage} alt="user-image" className="w-16 h-16" />
        <div className="flex flex-col items-center">
          <span className="text-black font-semibold text-lg">
            {t("auth.welcomeBack")}
          </span>
          <span className="text-gray-500 font-normal text-base">
            {t("auth.enterDetails")}
          </span>
        </div>

        <form
          className="w-full flex flex-col gap-4 items-center"
          onSubmit={handleSubmit(onSubmit)}
        >
          <FormInput
            label={t("common.email")}
            name="email"
            type="email"
            register={register}
            errors={errors}
            leftIcon={<Mail size={20} />}
            placeholder={t("auth.enterEmail")}
          />
          <FormInput
            label={t("common.password")}
            name="password"
            type="password"
            register={register}
            errors={errors}
            leftIcon={<CircleHelp size={20} />}
            placeholder={t("auth.enterPassword")}
          />
          <div className="flex justify-between w-full">
            <FormCheckbox
              label={<>{t("auth.rememberMe")}</>}
              name="rememberMe"
              register={register}
              errors={errors}
            />
            <Link to="forget-password" className="text-gray-500">
              {t("auth.forgotPassword")}?
            </Link>
          </div>

          <Button
            type="submit"
            fullWidth
            isLoading={isSubmitting}
            disabled={isSubmitting}
            icon={<LogIn size={16} />}
          >
            {t("common.login")}
          </Button>
        </form>

        <span className="text-gray-500">
          {t("auth.noAccount")}
          <Link to="/merchant-registration">{t("auth.signUp")}</Link>
        </span>
      </div>
    </div>
  );
};

export default LoginForm;
